// @ts-nocheck
import React from 'react';
import classNames from 'classnames';
import {
  LuWifi,
  LuWifiOff,
  LuClock,
  LuActivity,
  LuUpload,
  LuInfo,
  LuZap,
} from 'react-icons/lu';

import {
  SMART_COOLER_STATUS_CLASSES,
  VIEW_SC_STRINGS,
} from '@/constant/smartCooler/smartCooler.constant';
import { tid } from '@/utils/testId';
import { SMART_COOLER_TEST_IDS as TID } from '@/constant/testIds/smartCooler.testIds';
import { APP_BG, APP_BORDER, APP_TEXT } from '@/constant/theme/colors.constant';
import { getDateTimeAgo } from '@/utils/dateTime';
import { Text } from '@/components/atoms/AppText';
import AppButton from '@/components/atoms/AppButton';
import { TimelineItem } from '@/components/elements/TimelineItem';
import { SectionCard } from '@/components/organism/Cards/SectionCard';
import { ISmartCooler } from '../smartCooler.interface';

interface Props {
  item: ISmartCooler;
  isConnected: boolean;
  isOnline: boolean;
  onOpenFetchModal: () => void;
  onSwitchToLogs: () => void;
}

export const ViewSCConnectionTab: React.FC<Props> = ({
  item,
  isConnected,
  isOnline,
  onOpenFetchModal,
  onSwitchToLogs,
}) => {
  return (
    <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 items-stretch">
      {/* Timeline panel */}
      <div className="sm:col-span-2 flex flex-col">
        <SectionCard
          title={VIEW_SC_STRINGS.CONN_TIMELINE_TITLE}
          icon={<LuZap className="w-4 h-4" />}
          subtitle={
            VIEW_SC_STRINGS.CONN_TIMELINE_SUB ??
            'Recent device connectivity events'
          }
          bodyClassName="p-4"
          className="h-full"
        >
          <TimelineItem
            card
            icon={<LuWifi className="w-3.5 h-3.5 text-white" />}
            accent="bg-emerald-500"
            label={VIEW_SC_STRINGS.CONN_LAST_CONNECTED}
            value={item.lastConnectedDtm}
          />
          <TimelineItem
            card
            icon={<LuWifiOff className="w-3.5 h-3.5 text-white" />}
            accent="bg-red-500"
            label={VIEW_SC_STRINGS.CONN_LAST_DISCONNECTED}
            value={item.lastDisconnectedDtm}
          />
          <TimelineItem
            card
            icon={<LuActivity className="w-3.5 h-3.5 text-white" />}
            accent="bg-sky-500"
            label={VIEW_SC_STRINGS.CONN_LAST_HEARTBEAT}
            value={item.lastHeartbeatDtm}
            last
          />
        </SectionCard>
      </div>

      {/* Status hero panel */}
      <div
        className={classNames(
          'flex flex-col rounded-2xl border overflow-hidden h-full',
          APP_BORDER.soft,
          isConnected
            ? SMART_COOLER_STATUS_CLASSES.connected.panelBorder
            : SMART_COOLER_STATUS_CLASSES.disconnected.panelBorder,
        )}
      >
        {/* Hero block */}
        <div
          className={classNames(
            'relative flex flex-col items-center gap-4 px-5 py-10 overflow-hidden',
            isConnected
              ? SMART_COOLER_STATUS_CLASSES.connected.heroBg
              : SMART_COOLER_STATUS_CLASSES.disconnected.heroBg,
          )}
        >
          <div
            className={classNames(
              'pointer-events-none absolute inset-0 opacity-20 dark:opacity-10',
              isConnected
                ? SMART_COOLER_STATUS_CLASSES.connected.glowBg
                : SMART_COOLER_STATUS_CLASSES.disconnected.glowBg,
            )}
            style={{ filter: 'blur(40px)', transform: 'scale(1.4)' }}
          />

          <div
            className={classNames(
              'relative flex h-20 w-20 items-center justify-center rounded-full ring-4',
              isConnected
                ? SMART_COOLER_STATUS_CLASSES.connected.iconRing
                : SMART_COOLER_STATUS_CLASSES.disconnected.iconRing,
            )}
          >
            {isConnected ? (
              <LuWifi
                className={classNames(
                  'w-9 h-9',
                  SMART_COOLER_STATUS_CLASSES.connected.iconColor,
                )}
              />
            ) : (
              <LuWifiOff
                className={classNames(
                  'w-9 h-9',
                  SMART_COOLER_STATUS_CLASSES.disconnected.iconColor,
                )}
              />
            )}
            {isConnected && (
              <span className="absolute -top-0.5 -right-0.5 flex h-3 w-3">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-70" />
                <span className="relative inline-flex h-3 w-3 rounded-full bg-emerald-500" />
              </span>
            )}
          </div>

          {item.lastConnectedDtm && (
            <div
              className={classNames(
                'relative inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-[11px] font-semibold',
                isConnected
                  ? SMART_COOLER_STATUS_CLASSES.connected.lastSeenPill
                  : SMART_COOLER_STATUS_CLASSES.disconnected.lastSeenPill,
              )}
            >
              <LuClock className="w-3 h-3 shrink-0" />
              {getDateTimeAgo(item.lastConnectedDtm)}
            </div>
          )}
        </div>

        {/* Stats row */}
        <div
          className={classNames(
            'grid grid-cols-2 divide-x border-y',
            APP_BORDER.divideSoft,
            APP_BORDER.filterRow,
            APP_BG.surface,
          )}
        >
          <div className="flex flex-col items-center gap-0.5 px-3 py-3">
            <Text
              as="p"
              size="xs"
              color="muted"
              className="uppercase tracking-widest"
            >
              {VIEW_SC_STRINGS.CONN_STATS_SYSTEM}
            </Text>
            <Text
              as="p"
              size="sm"
              weight="bold"
              className={
                isOnline
                  ? SMART_COOLER_STATUS_CLASSES.connected.systemText
                  : SMART_COOLER_STATUS_CLASSES.disconnected.systemText
              }
            >
              {item.systemState ?? '—'}
            </Text>
          </div>
          <div className="flex flex-col items-center gap-0.5 px-3 py-3">
            <Text
              as="p"
              size="xs"
              color="muted"
              className="uppercase tracking-widest"
            >
              {VIEW_SC_STRINGS.CONN_STATS_HEARTBEAT}
            </Text>
            <Text
              as="p"
              size="sm"
              weight="bold"
              color="default"
              className="text-center"
            >
              {item.lastHeartbeatDtm
                ? getDateTimeAgo(item.lastHeartbeatDtm)
                : '—'}
            </Text>
          </div>
        </div>

        {/* Fetch logs action */}
        <div
          className={classNames(
            'flex flex-col gap-3 p-4 flex-1 justify-end',
            APP_BG.base,
          )}
        >
          <div className="flex items-start gap-2.5">
            <div
              className={classNames(
                'flex h-7 w-7 shrink-0 items-center justify-center rounded-lg mt-0.5',
                APP_BG.muted,
              )}
            >
              <LuUpload className={`w-3.5 h-3.5 ${APP_TEXT.soft}`} />
            </div>
            <div>
              <Text as="p" size="sm" weight="bold" color="default">
                {VIEW_SC_STRINGS.CONN_FETCH_TITLE}
              </Text>
              <Text
                as="p"
                size="xs"
                color="muted"
                className="leading-relaxed mt-0.5"
              >
                {VIEW_SC_STRINGS.CONN_FETCH_DESC}
              </Text>
            </div>
          </div>
          <AppButton
            data-testid={tid(TID.CONN_FETCH_BTN)}
            variant={isConnected ? 'info' : 'neutral'}
            size="sm"
            fullWidth
            disabled={!isConnected}
            icon={<LuUpload className="w-3.5 h-3.5" />}
            onClick={onOpenFetchModal}
          >
            {isConnected
              ? VIEW_SC_STRINGS.CONN_FETCH_BTN_ONLINE
              : VIEW_SC_STRINGS.CONN_FETCH_BTN_OFFLINE}
          </AppButton>
          <div
            className={classNames(
              'flex items-center gap-1.5 rounded-xl border px-3 py-2',
              APP_BORDER.soft,
              APP_BG.surface,
            )}
          >
            <LuInfo className={`w-3 h-3 shrink-0 ${APP_TEXT.faint}`} />
            <Text as="p" size="xs" color="muted">
              {VIEW_SC_STRINGS.CONN_VIEW_LOGS_PREFIX}{' '}
              <button
                data-testid={tid(TID.CONN_VIEW_LOGS_BTN)}
                onClick={onSwitchToLogs}
                className="text-primary font-semibold underline underline-offset-2"
              >
                {VIEW_SC_STRINGS.CONN_VIEW_LOGS_LABEL}
              </button>{' '}
              {VIEW_SC_STRINGS.CONN_VIEW_LOGS_SUFFIX}
            </Text>
          </div>
        </div>
      </div>
    </div>
  );
};
